import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Shop, ShopBarber } from '../types/shop';
import { Barber, BarberServices } from '../types/barber';
import {
  getShopById,
  getBarberById,
} from '../middleware/registration_service/getRequests';
import {
  getShopSlotsById,
  getShopSlotsByShopIdAndBarberId,
  getBarberSlotsByBarberId,
  getShopTotalBookingCount,
  getBarberTotalBookingCount,
} from '../middleware/reservation_service/getRequests';
import { CreateBookingSlot } from '../types/bookingSlot';
import { GalleryCarouselImage } from '../pages/components/GalleryCarousel';

export type PublicEntityType = 'SHOP' | 'BARBER';

/** A service offering paired with the shop barber who performs it (shop profiles only). */
export interface ShopServiceOffering {
  service: BarberServices;
  barber: ShopBarber;
}

interface PublicProfileContextValue {
  entityType: PublicEntityType;
  loading: boolean;
  error: string | null;
  shop: Shop | null;
  barber: Barber | null;
  galleryImages: GalleryCarouselImage[];
  /** Shop profiles only: every service offered, paired with the barber who provides it. */
  shopServices: ShopServiceOffering[];
  /** Barber profiles only: the independent barber's own service list. */
  barberServices: BarberServices[];
  /** The total number of completed bookings for this profile. */
  completedBookingsCount: number;
  /**
   * Returns cached appointment slots for the profile (optionally filtered to
   * a single shop barber), fetching from the reservation service only on
   * first request per key to avoid repeat network calls during a booking
   * session.
   */
  getSlots: (shopBarberId?: bigint) => Promise<CreateBookingSlot[]>;
  /** Removes a slot from every cached slot list once it has been booked. */
  removeSlotFromCache: (slotId: string) => void;
}

const PublicProfileContext = createContext<PublicProfileContextValue | null>(
  null
);

export const usePublicProfile = (): PublicProfileContextValue => {
  const ctx = useContext(PublicProfileContext);
  if (!ctx) {
    throw new Error(
      'usePublicProfile must be used within a PublicProfileProvider'
    );
  }
  return ctx;
};

const mapRawSlots = (rawSlots: any[], shopId?: bigint): CreateBookingSlot[] =>
  rawSlots
    .filter((slot: any) => slot.status === 'AVAILABLE')
    .map((slot: any) => ({
      id: slot.id,
      barber_id: BigInt(slot.barber_id),
      service_id: BigInt(slot.service_id),
      start_time: new Date(`${slot.slot_date}T${slot.start_time}:00`),
      status: slot.status,
      slot_date: new Date(slot.slot_date),
      ...(shopId !== undefined && { shop_id: shopId }),
    }));

interface PublicProfileProviderProps {
  entityType: PublicEntityType;
  id: bigint;
  children: React.ReactNode;
}

export const PublicProfileProvider: React.FC<PublicProfileProviderProps> = ({
  entityType,
  id,
  children,
}) => {
  const [shop, setShop] = useState<Shop | null>(null);
  const [barber, setBarber] = useState<Barber | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [completedBookingsCount, setCompletedBookingsCount] =
    useState<number>(0);

  // Keyed by `all` (every barber) or a shop barber ID, so each distinct
  // slot query is only ever requested once per booking session.
  const slotsCacheRef = useRef<Map<string, CreateBookingSlot[]>>(new Map());
  const slotsInFlightRef = useRef<Map<string, Promise<CreateBookingSlot[]>>>(
    new Map()
  );

  useEffect(() => {
    let cancelled = false;
    slotsCacheRef.current = new Map();
    slotsInFlightRef.current = new Map();

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        if (entityType === 'SHOP') {
          const [shopRes, bookingRes] = await Promise.all([
            getShopById(id),
            getShopTotalBookingCount(id),
          ]);
          if (!cancelled) {
            setShop(shopRes.data);
            setBarber(null);
            setCompletedBookingsCount(bookingRes.data.count);
          }
        } else {
          const [barberRes, bookingRes] = await Promise.all([
            getBarberById(id),
            getBarberTotalBookingCount(id),
          ]);
          if (!cancelled) {
            setBarber(barberRes.data);
            setShop(null);
            setCompletedBookingsCount(bookingRes.data.count);
          }
        }
      } catch (err) {
        console.error('Failed to load public profile:', err);
        if (!cancelled) {
          setError('Unable to load this profile. Please try again later.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [entityType, id]);

  const getSlots = useCallback(
    async (shopBarberId?: bigint): Promise<CreateBookingSlot[]> => {
      const cacheKey = shopBarberId ? shopBarberId.toString() : 'all';
      const cached = slotsCacheRef.current.get(cacheKey);
      if (cached) return cached;

      const inFlight = slotsInFlightRef.current.get(cacheKey);
      if (inFlight) return inFlight;

      const request = (async () => {
        try {
          const response =
            entityType === 'SHOP'
              ? shopBarberId
                ? await getShopSlotsByShopIdAndBarberId(id, shopBarberId)
                : await getShopSlotsById(id)
              : await getBarberSlotsByBarberId(id);

          const rawSlots = Array.isArray(response.data) ? response.data : [];
          const slots = mapRawSlots(
            rawSlots,
            entityType === 'SHOP' ? id : undefined
          );
          slotsCacheRef.current.set(cacheKey, slots);
          return slots;
        } finally {
          slotsInFlightRef.current.delete(cacheKey);
        }
      })();

      slotsInFlightRef.current.set(cacheKey, request);
      return request;
    },
    [entityType, id]
  );

  const removeSlotFromCache = useCallback((slotId: string) => {
    slotsCacheRef.current.forEach((slots, key) => {
      slotsCacheRef.current.set(
        key,
        slots.filter((s) => s.id !== slotId)
      );
    });
  }, []);

  const galleryImages: GalleryCarouselImage[] = useMemo(() => {
    if (entityType === 'SHOP') {
      return (shop?.shopGalleryImageName || []).map((img) => ({
        id: img.id.toString(),
        url: img.imageSelfLink,
      }));
    }
    return (barber?.galleryImageName || []).map((img) => ({
      id: img.id.toString(),
      url: img.imageSelfLink,
    }));
  }, [entityType, shop, barber]);

  const shopServices: ShopServiceOffering[] = useMemo(() => {
    if (entityType !== 'SHOP' || !shop?.shopBarbers) return [];
    return shop.shopBarbers.flatMap((shopBarber) =>
      (shopBarber.barberShopServices || []).map((service) => ({
        service,
        barber: shopBarber,
      }))
    );
  }, [entityType, shop]);

  const barberServices: BarberServices[] = useMemo(() => {
    if (entityType !== 'BARBER') return [];
    return barber?.barberServices || [];
  }, [entityType, barber]);

  const value = useMemo<PublicProfileContextValue>(
    () => ({
      entityType,
      loading,
      error,
      shop,
      barber,
      galleryImages,
      shopServices,
      barberServices,
      completedBookingsCount,
      getSlots,
      removeSlotFromCache,
    }),
    [
      entityType,
      loading,
      error,
      shop,
      barber,
      galleryImages,
      shopServices,
      barberServices,
      completedBookingsCount,
      getSlots,
      removeSlotFromCache,
    ]
  );

  return (
    <PublicProfileContext.Provider value={value}>
      {children}
    </PublicProfileContext.Provider>
  );
};
